/**
 * garabat-referral.js
 * ---------------------------------------------------------------------
 * Orquestador de navegador del cliente de referral del theme. Construye las
 * dependencias reales del entorno (fetch, crypto, storage, cookies, location)
 * y delega TODA la lógica en `GarabatReferralCommon.createClient(deps)` (ver
 * garabat-referral-common.js), que se carga antes que este script.
 *
 * Responsabilidades exclusivas de este archivo (glue de DOM):
 *   - Leer window.GarabatReferralConfig y construir `deps`.
 *   - Cargar el loader global de Viral Loops (widgets v2) con el
 *     viralLoopsCampaignId servido por el backend (nunca el public token).
 *   - Re-aserción de note attributes justo antes de ir a checkout.
 *
 * Modo de atribución:
 *   - `disabled`  → kill switch: no valida ni escribe atributos firmados.
 *   - `signed_v1` → flujo firmado (clientNonce → validate → note attrs).
 *   - `auto`/legacy → comportamiento legacy equivalente al histórico.
 *
 * Nunca incluye VIRAL_LOOPS_API_TOKEN ni SHOPIFY_ADMIN_ACCESS_TOKEN: son
 * secretos de servidor y jamás tocan el navegador. El token de atribución es
 * opaco y solo viaja como note attribute; nunca en URLs ni consola.
 * ---------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CONFIG = window.GarabatReferralConfig || {};
  var Common = window.GarabatReferralCommon;
  if (!Common || typeof Common.createClient !== 'function') return;

  var VIRAL_LOOPS_LOADER_ID = 'viral-loops-loader';
  var VIRAL_LOOPS_LOADER_SRC = 'https://app.viral-loops.com/widgetsV2/core/loader.js';

  function makeUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      return Common.fallbackUuidFrom(function (arr) { window.crypto.getRandomValues(arr); });
    }
    throw new Error('secure_context_required'); // sin crypto seguro no se genera nonce
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /** Carga el loader global de Viral Loops una sola vez (sin duplicar <script>). */
  function loadViralLoopsSnippet(viralLoopsId) {
    if (!viralLoopsId) return;
    var existing = document.getElementById(VIRAL_LOOPS_LOADER_ID);
    if (existing) {
      existing.setAttribute('data-campaign-id', viralLoopsId);
      return;
    }
    var script = document.createElement('script');
    script.id = VIRAL_LOOPS_LOADER_ID;
    script.setAttribute('data-campaign-id', viralLoopsId);
    script.src = VIRAL_LOOPS_LOADER_SRC;
    script.async = true;
    document.head.appendChild(script);
  }

  // Exclusión mutua cross-tab de las mutaciones del carrito vía Web Locks.
  // Fallback documentado (navegadores sin `navigator.locks`): el módulo común
  // degrada a generación compartida + reconciliación posterior, que NO ofrece
  // exclusión atómica entre pestañas (solo serialización intra-pestaña).
  var cartLocks =
    (window.navigator && window.navigator.locks && typeof window.navigator.locks.request === 'function')
      ? { request: function (name, cb) { return window.navigator.locks.request(name, cb); } }
      : null;

  var deps = {
    fetch: window.fetch ? window.fetch.bind(window) : null,
    now: function () { return Date.now(); },
    uuid: makeUuid,
    localStorage: safeStorage(function () { return window.localStorage; }),
    sessionStorage: safeStorage(function () { return window.sessionStorage; }),
    getCookie: getCookie,
    setRawCookie: function (raw) { document.cookie = raw; },
    location: { href: window.location.href, search: window.location.search },
    config: CONFIG,
    locks: cartLocks,
    onCampaignConfig: function (serverConfig) {
      loadViralLoopsSnippet(serverConfig && serverConfig.viralLoopsCampaignId);
    },
  };

  // Acceder a window.localStorage puede lanzar en algunos navegadores/modo
  // privado incluso al leer la propiedad: lo envolvemos.
  function safeStorage(getter) {
    try { return getter() || null; } catch (_e) { return null; }
  }

  var client = Common.createClient(deps);

  // --- Página de campaña (HTML pegado en Shopify > Páginas) ---------------
  // El HTML de la landing no lleva <script> (el editor de Páginas puede
  // quitarlo): aquí se engancha el formulario por id. La configuración de la
  // campaña viaja en campos ocultos del propio formulario (campaign_slug,
  // product_url, share_text), que el editor respeta mejor que los data-*.
  var JOIN_MESSAGES = {
    invalid_input: 'Escribe tu nombre y un email válido para conseguir tu enlace.',
    rate_limited: 'Demasiados intentos seguidos. Espera un minuto y vuelve a probar.',
    campaign_unavailable: 'Esta campaña no está activa ahora mismo.',
    code_pending: 'Te hemos apuntado, pero tu enlace aún se está generando. Vuelve a enviar el formulario en unos minutos con el mismo email.',
    bad_campaign: 'Esta página no está bien configurada. Avísanos en info@garabat.es.',
    not_configured: 'Esta página no está bien configurada. Avísanos en info@garabat.es.',
    unavailable: 'No hemos podido generar tu enlace ahora mismo. Inténtalo de nuevo en unos minutos.',
  };

  function byId(id) { return document.getElementById(id); }
  function fieldValue(form, name) { var el = form.elements[name]; return el ? String(el.value || '') : ''; }

  function copyText(text, button) {
    var done = function () {
      var original = button.getAttribute('data-label') || button.textContent;
      button.setAttribute('data-label', original);
      button.textContent = 'Copiado';
      setTimeout(function () { button.textContent = original; }, 1800);
    };
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text).then(done, function () {});
    }
  }

  function bindJoinForm() {
    var form = byId('garabat-join-form');
    var result = byId('garabat-join-result');
    if (!form || !result) return;
    var errorEl = byId('garabat-join-error');
    var submit = form.querySelector('button[type="submit"]');

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (submit) submit.disabled = true;
      var consent = form.elements.marketing_consent;
      client.join({
        campaignSlug: fieldValue(form, 'campaign_slug'),
        productUrl: fieldValue(form, 'product_url'),
        firstName: fieldValue(form, 'first_name'),
        email: fieldValue(form, 'email'),
        marketingConsent: !!(consent && consent.checked),
      }).then(function (res) {
        if (submit) submit.disabled = false;
        if (!res.ok || !res.link) {
          if (errorEl) { errorEl.textContent = JOIN_MESSAGES[res.reason] || JOIN_MESSAGES.unavailable; errorEl.hidden = false; }
          return;
        }
        var link = res.link, code = res.discountCode || '';
        var message = Common.fillShareTemplate(fieldValue(form, 'share_text'), { link: link, code: code });
        if (byId('garabat-join-link')) byId('garabat-join-link').textContent = link;
        if (byId('garabat-join-code')) byId('garabat-join-code').textContent = code;
        if (byId('garabat-join-code-row')) byId('garabat-join-code-row').hidden = !code;
        var wa = byId('garabat-share-whatsapp');
        if (wa) wa.href = 'https://wa.me/?text=' + encodeURIComponent(message);
        var mail = byId('garabat-share-email');
        if (mail) mail.href = 'mailto:?subject=' + encodeURIComponent(document.title) + '&body=' + encodeURIComponent(message);
        var copyLink = byId('garabat-copy-link');
        if (copyLink) copyLink.onclick = function () { copyText(link, copyLink); };
        var copyCode = byId('garabat-copy-code');
        if (copyCode) copyCode.onclick = function () { copyText(code, copyCode); };
        form.hidden = true;
        result.hidden = false;
        try { result.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_e) {}
      }, function () {
        if (submit) submit.disabled = false;
        if (errorEl) { errorEl.textContent = JOIN_MESSAGES.unavailable; errorEl.hidden = false; }
      });
    });
  }

  function init() {
    try { bindJoinForm(); } catch (_e) {}

    // La lógica (captura de ref, modo, legacy/signed) vive en el módulo común.
    // No await bloqueante: la compra nunca se bloquea por esto.
    try { client.run(); } catch (_e) {}

    // Re-aserción de atributos antes de ir a checkout (algunos temas reinician
    // el cart form entre secciones). Best-effort, sin bloquear la navegación.
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (form && form.action && /\/cart/.test(form.action)) {
        try { client.reassertCart(); } catch (_e) {}
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
